import { createStore, setStateHelper } from "@newrai/ant-design";
import { StoreApi, UseBoundStore } from "zustand";

export interface UserStore extends GetUserByToken {
  groupDefault: { order: number };
}

interface StoreState {
  userData: UserStore;
  setUserData: (updater: ((oldState: UserStore) => UserStore) | UserStore) => void;
  companySelected: CompanySelected;
  setCompanySelected: (companySelected?: CompanySelected | undefined) => void;
  accountingFirmSelected: AccountingFirmSelected;
  setAccountingFirmSelected: (accountingFirmSelected?: AccountingFirmSelected | undefined) => void;
  isCompanySelectOpen: boolean;
  setIsCompanySelectOpen: (isCompanySelectOpen: boolean) => void;
  companySoftware: SoftwareType;
  setCompanySoftware: (companySoftware: SoftwareType) => void;
  createUpdateUserLoading: boolean;
  setCreateUpdateUserLoading: (createUpdateUserLoading: boolean) => void;
  saveButtonWasClicked: boolean;
  setSaveButtonWasClicked: (saveButtonWasClicked: boolean) => void;
  dataChanged: boolean;
  setDataChanged: (dataChanged: boolean) => void;
  externalServiceData: OptionType[];
  setExternalServiceData: (externalServiceData: OptionType) => void;
  currentCycle: CycleType;
  setCurrentCycle: (currentCycle: CycleType) => void;
  resetStore: () => void;
}

const initialState = {
  userData: undefined,
  companySelected: undefined,
  accountingFirmSelected: undefined,
  isCompanySelectOpen: false,
  companySoftware: undefined,
  createUpdateUserLoading: undefined,
  saveButtonWasClicked: false,
  dataChanged: false,
  externalServiceData: undefined,
  currentCycle: undefined,
};

const useStore: UseBoundStore<StoreApi<StoreState>> = createStore<StoreState>(
  (set: (fn: (draft: StoreState) => void) => void, get: () => StoreState) => ({
    ...initialState,
    resetStore: () => {
      set(() => ({
        ...initialState,
      }));
    },
    setUserData: (updater: ((oldState: UserStore) => UserStore) | UserStore) => {
      set(state => {
        return {
          ...state,
          userData: typeof updater === "function" ? updater(state.userData || {}) : updater,
        };
      });
    },
    setCompanySoftware: (companySoftware: SoftwareType) => {
      setStateHelper("companySoftware", companySoftware, set, get);
    },
    setCurrentCycle: (currentCycle: CycleType) => {
      setStateHelper("currentCycle", currentCycle, set, get);
    },
    setCompanySelected: (companySelected: CompanySelected) => {
      setStateHelper("companySelected", companySelected, set, get);
    },
    setAccountingFirmSelected: (accountingFirmSelected: AccountingFirmSelected) => {
      setStateHelper("accountingFirmSelected", accountingFirmSelected, set, get);
    },
    setIsCompanySelectOpen: (isCompanySelectOpen: boolean) => {
      setStateHelper("isCompanySelectOpen", isCompanySelectOpen, set, get);
    },
    setCreateUpdateUserLoading: (createUpdateUserLoading: boolean) => {
      setStateHelper("createUpdateUserLoading", createUpdateUserLoading, set, get);
    },
    setExternalServiceData: (externalServiceData: OptionType[]) => {
      setStateHelper("externalServiceData", externalServiceData, set, get);
    },
    setSaveButtonWasClicked: (saveButtonWasClicked: boolean) => {
      setStateHelper("saveButtonWasClicked", saveButtonWasClicked, set, get);
    },
    setDataChanged: (dataChanged: boolean) => {
      setStateHelper("dataChanged", dataChanged, set, get);
    },
  }),
);

export default useStore;
